import { Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

const ACCEPTED_TYPES = ["image/svg+xml", "image/png", "image/jpeg"];
// Must stay in sync with backend MAX_ORGANIZATION_LOGO_SIZE.
const MAX_BYTES = 2 * 1024 * 1024;

export interface LogoUploaderProps {
  value: string | null;
  /** Called when the user picks a file. Parent owns the actual upload. */
  onFile: (file: File) => void;
  onClear: () => void;
  disabled?: boolean;
  isUploading?: boolean;
}

export function LogoUploader({
  value,
  onFile,
  onClear,
  disabled = false,
  isUploading = false,
}: LogoUploaderProps) {
  const { t } = useTranslations("cards");
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validate = (file: File): string | null => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return t("branding_logo_error_format");
    }
    if (file.size > MAX_BYTES) {
      return t("branding_logo_error_size");
    }
    return null;
  };

  const handleFile = (file: File | null | undefined) => {
    setError(null);
    if (!file) return;
    const err = validate(file);
    if (err) {
      setError(err);
      return;
    }
    onFile(file);
  };

  const onDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDrag(false);
    if (disabled) return;
    handleFile(e.dataTransfer.files?.[0]);
  };

  if (value) {
    return (
      <div>
        {/* Against its own width (`@container`): narrow, the actions drop under the description, lined up
            with it, instead of squeezing it to a word per line. */}
        <div className="@container rounded-lg border border-border bg-card p-3">
          <div className="flex flex-col gap-3 @md:flex-row @md:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-foreground/90">
                <img src={value} alt="" className="max-h-10 max-w-12 object-contain" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground">{t("branding_logo_loaded_title")}</div>
                <div className="text-xs text-muted-foreground">{t("branding_logo_loaded_description")}</div>
              </div>
            </div>
            <div className="ml-17 flex items-center gap-1 @md:ml-0">
              <ColocaButton
                variant="default"
                size="sm"
                disabled={disabled || isUploading}
                onClick={() => inputRef.current?.click()}
              >
                {t("branding_logo_button_replace")}
              </ColocaButton>
              <ColocaIconButton
                variant="ghost"
                size="sm"
                icon={Trash2}
                disabled={disabled || isUploading}
                onClick={onClear}
                aria-label={t("branding_logo_button_remove")}
                className="text-destructive hover:bg-destructive/10"
              />
            </div>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
        {error && <div className="mt-2 text-xs text-destructive">{error}</div>}
      </div>
    );
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <button
        type="button"
        disabled={disabled || isUploading}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={cn(
          "flex w-full items-center gap-4 rounded-lg border-2 border-dashed p-5 text-left transition-all",
          (disabled || isUploading) && "cursor-not-allowed opacity-60",
          drag ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted",
        )}
      >
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Upload size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-foreground">
            {isUploading ? t("branding_logo_uploading") : t("branding_logo_uploader_idle")}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {t("branding_logo_uploader_constraints")}
          </div>
        </div>
      </button>
      {error && <div className="mt-2 text-xs text-destructive">{error}</div>}
    </div>
  );
}
